import { Alert, Box, Button, Stack, TextField } from "@mui/material";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { createServiceForProduct } from "../api/client";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";
import { useHierarchyContext } from "../contexts/HierarchyContext";

export function PageServiceNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hierarchy } = useHierarchyContext();
  const productSlug = hierarchy?.product?.slug ?? null;

  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!productSlug) return;

    const s = slug.trim();
    const n = name.trim();
    const d = description.trim();
    if (!s || !n) return;

    try {
      setLoading(true);
      setError(null);
      const created = await createServiceForProduct(
        productSlug,
        { slug: s, name: n, description: d ? d : null },
        undefined,
      );
      await queryClient.refetchQueries({ queryKey: ["hierarchy", "full"] });
      navigate(`/services/${encodeURIComponent(created.slug)}/health`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create service");
    } finally {
      setLoading(false);
    }
  };

  if (!productSlug) {
    return (
      <Alert severity="warning">
        Missing product in URL. Please navigate from a tenant’s products list.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Panel
        title="Create Service"
        subtitle={`Product: ${productSlug}`}
        toolbar={
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" form="service-new-form" disabled={loading}>
              Create
            </Button>
          </Stack>
        }
      >
        {error ? <PageErrorState message={error} sx={{ mb: 1.5 }} /> : null}
        <Box
          id="service-new-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxWidth: 720,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Service slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g., api"
            InputProps={{ className: "om-text-mono" }}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Service name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., API"
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this service do?"
            multiline
            minRows={2}
          />
        </Box>
      </Panel>

      <Alert severity="info">
        Tip: after creating a service, you can generate an ingress token on the service overview
        page.
      </Alert>
    </Box>
  );
}
